import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button
} from "@chakra-ui/react";
import React from "react";
import hotToast from "react-hot-toast";
import { formatSnakeCaseToTitleCase } from "../../utils/string";
import FormikFormInputControl from "./FormikFormInputControl";

type ObjectModalProps = {
    isOpen: boolean;
    onClose: () => void;
    argument: any;
    formikProps: any;
    onChange: (result: any) => void;
    editMode?: boolean;
};

enum ARGUMENT_TYPES {
    OBJECT = "object"
}

// eslint-disable-next-line no-undef
const displayInput = (param: any, formikProps: any): JSX.Element => {
    switch (param.type) {
        case ARGUMENT_TYPES.OBJECT:
            return (
                <div>
                    {Object.entries(param.properties || {}).map(([key, value]) => (
                        <div key={key} className="grid grid-cols-6 gap-2 items-center">
                            <div className="col-span-6 ml-4 border-l-2 border-gray-500 pl-4">
                                {displayInput(
                                    { key, ...(typeof value === "object" ? value : {}) },
                                    formikProps
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        default:
            return (
                <FormikFormInputControl
                    key={`arg-control-${param.key}`}
                    name={param.key}
                    argument={{
                        ...param,
                        label:
                            param.key === "value_schema"
                                ? param.placeholder
                                : formatSnakeCaseToTitleCase(param.key)
                    }}
                    formikProps={formikProps}
                    showConvertToUserInputCheckbox={false}
                />
            );
    }
};

// eslint-disable-next-line no-undef
const handleKeyPlaceholderCase = (argument: any, formikProps: any): JSX.Element => {
    return (
        <div>
            <FormikFormInputControl
                key={`arg-key-placeholder-${argument.key}`}
                name={argument.key_placeholder}
                argument={{
                    type: "string",
                    placeholder: argument.key_placeholder,
                    key: argument.key_placeholder,
                    label: argument.key_placeholder,
                    required: true
                }}
                formikProps={formikProps}
                showConvertToUserInputCheckbox={false}
            />
            {argument.value_schema ? (
                displayInput({ key: "value_schema", ...argument.value_schema }, formikProps)
            ) : argument.properties ? (
                argument.properties.type ? (
                    <div className="ml-4 border-l-2 border-gray-500 pl-4">
                        {Object.entries(argument.properties.properties || {}).map(
                            ([key, value]) => (
                                <div key={key} className="mb-4">
                                    <div className="mt-1">
                                        {displayInput(
                                            { key, ...(typeof value === "object" ? value : {}) },
                                            formikProps
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                ) : (
                    <div className="ml-4 border-l-2 border-gray-500 pl-4">
                        {Object.entries(argument.properties || {}).map(([key, value]) => (
                            <div key={key} className="mb-4">
                                <div className="mt-1">
                                    {displayInput(
                                        { key, ...(typeof value === "object" ? value : {}) },
                                        formikProps
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : null}
        </div>
    );
};

const renderInputs = (argument: any, formikProps: any) => {
    if (argument.key_placeholder === undefined) {
        return (
            <div>
                {Object.entries(argument.properties || {}).map(([key, value]) => (
                    <div key={key} className="mb-4">
                        <div className="mt-1">
                            {displayInput(
                                { key, ...(typeof value === "object" ? value : {}) },
                                formikProps
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    return handleKeyPlaceholderCase(argument, formikProps);
};

const ObjectModal: React.FC<ObjectModalProps> = ({
    isOpen,
    onClose,
    argument,
    formikProps,
    onChange,
    editMode
}) => {
    const handleAdd = () => {
        let formResult: any = {};
        let finalResult: any = {};
        const missingFields: string[] = [];

        // Process all properties and ensure timezone fields are included
        const processProperties = (properties: any, resultObj: any) => {
            Object.entries(properties).forEach(([key, config]: [string, any]) => {
                // Check for required fields
                if (config.required && !formikProps.values[key]) {
                    missingFields.push(key);
                }

                // Handle timezone fields specially
                if (config.type === "time_zone") {
                    if (formikProps.values[key]) {
                        // If we have a direct value, use it
                        resultObj[key] = formikProps.values[key];
                    } else if (
                        formikProps.values.label &&
                        formikProps.values.value &&
                        formikProps.values.offset
                    ) {
                        // If we have the component parts, construct the timezone object
                        resultObj[key] = {
                            label: formikProps.values.label,
                            value: formikProps.values.value,
                            offset: formikProps.values.offset
                        };
                    }
                }
                // For non-timezone fields, only include if they have a value
                else if (formikProps.values[key] !== undefined) {
                    resultObj[key] = formikProps.values[key];
                }
            });
        };

        // Validation for required fields when key_placeholder is defined (multiple objects)
        if (argument.key_placeholder !== undefined) {
            // Validate the dynamic key
            if (!formikProps.values[argument.key_placeholder]) {
                missingFields.push(argument.key_placeholder);
            }

            if (argument.value_schema) {
                if (argument.value_schema.required && !formikProps.values["value_schema"]) {
                    missingFields.push("value_schema");
                }
                formResult = formikProps.values["value_schema"];
            } else if (argument.properties && argument.properties.properties) {
                // Process all properties in properties.properties
                processProperties(argument.properties.properties, formResult);
            } else if (argument.properties) {
                // Process all properties
                processProperties(argument.properties, formResult);
            }

            finalResult = {
                [argument.key]: { [formikProps.values[argument.key_placeholder]]: formResult }
            };
        } else {
            // Single object branch (only one allowed)
            const propKeys = Object.keys(argument.properties || {});
            if (
                propKeys.length === 1 &&
                argument.properties[propKeys[0]]?.type === ARGUMENT_TYPES.OBJECT
            ) {
                const childKey = propKeys[0];
                formResult[childKey] = {};
                processProperties(
                    argument.properties[childKey].properties || {},
                    formResult[childKey]
                );
            } else {
                processProperties(argument.properties || {}, formResult);
            }
            finalResult = { [argument.key]: formResult };
        }

        if (missingFields.length > 0) {
            hotToast.error(`Please fill all required fields: ${missingFields.join(", ")}`);
            return;
        }

        // If validation passes, pass the final result upward.
        onChange(finalResult);

        // Clear the modal values by resetting the fields in formikProps.
        if (argument.key_placeholder !== undefined) {
            formikProps.setFieldValue(argument.key_placeholder, "");
            if (argument.value_schema) {
                formikProps.setFieldValue("value_schema", "");
            } else if (argument.properties) {
                Object.keys(argument.properties).forEach((key) => {
                    formikProps.setFieldValue(key, "");
                });
            }
        } else {
            Object.keys(argument.properties || {}).forEach((key) => {
                formikProps.setFieldValue(key, "");
            });
        }

        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{argument.label}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <div className="min-h-[70px]">{renderInputs(argument, formikProps)}</div>
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="gray" variant={"ghost"} mr={3} onClick={onClose}>
                        Close
                    </Button>
                    <Button colorScheme="blue" color="black" onClick={handleAdd}>
                        {argument?.button_text ? (editMode ? "Edit" : "Add") : "Configure"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ObjectModal;
